/**
 * Font family picker for overlay text elements (title/subheader/body/category).
 *
 * value = font-library family SLUG or null (null = inherit up the cascade:
 * template → brand default → universal default → Inter). The pinned top row
 * shows what "Inherit" currently resolves to. Options render in their own
 * font — glyphs are real because FontLibraryProvider registered every
 * variant with document.fonts.
 *
 * Follows the TextStylePopover portal/positioning pattern.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../../styles/zLayers';
import type { OverlayTemplateConfig } from '../../../api/tendApi';
import { useFontLibrary } from '../../../contexts/FontLibraryContext';
import { useExclusivePopover } from './PopoverGroup';
import {
  FontElement,
  resolveInheritedFont,
} from '../../../utils/fontResolution';

interface FontPickerPopoverProps {
  /** Selected family slug at THIS layer, or null = inherit. */
  value: string | null;
  onChange: (slug: string | null) => void;
  element: FontElement;
  /**
   * The config of the layer ABOVE this one for the inherit hint. When editing
   * content, pass the active template's config; when editing a template,
   * pass null (templates inherit straight from brand/universal defaults).
   */
  inheritConfig?: Partial<OverlayTemplateConfig> | null;
  position?: 'above' | 'below' | 'auto';
  disabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const POPOVER_W = 230;
const POPOVER_H = 280;

type PopoverCoords = { top?: number; bottom?: number; left: number };

const FontPickerPopover: React.FC<FontPickerPopoverProps> = ({
  value,
  onChange,
  element,
  inheritConfig = null,
  position = 'above',
  disabled = false,
  onOpenChange,
}) => {
  const { manifest } = useFontLibrary();
  const [isOpen, setIsOpen] = useExclusivePopover();
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState<PopoverCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => { onOpenChange?.(isOpen); }, [isOpen, onOpenChange]);

  const calc = useCallback((): PopoverCoords | null => {
    if (!triggerRef.current) return null;
    const r = triggerRef.current.getBoundingClientRect();
    const parent = triggerRef.current.parentElement;
    const anchor = parent ? parent.getBoundingClientRect() : r;
    const goAbove = position === 'above' || (position === 'auto' && anchor.top > POPOVER_H + 10);
    // Keep the popover on-screen horizontally.
    const left = Math.min(r.left, window.innerWidth - POPOVER_W - 8);
    if (goAbove) {
      return { bottom: window.innerHeight - anchor.top, left };
    }
    return { top: anchor.bottom, left };
  }, [position]);

  useEffect(() => {
    if (!isOpen) return;
    setCoords(calc());
    const onScroll = () => setCoords(calc());
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, calc]);

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    const tid = setTimeout(() => document.addEventListener('mousedown', onClick), 10);
    return () => {
      clearTimeout(tid);
      document.removeEventListener('mousedown', onClick);
    };
  }, [isOpen, setIsOpen]);

  const inherited = useMemo(
    () => resolveInheritedFont(inheritConfig, element, manifest),
    [inheritConfig, element, manifest],
  );

  const selectedFamily = value
    ? manifest?.families.find((f) => f.slug === value) ?? null
    : null;

  const filtered = useMemo(() => {
    const families = manifest?.families ?? [];
    const q = search.trim().toLowerCase();
    return q ? families.filter((f) => f.name.toLowerCase().includes(q)) : families;
  }, [manifest, search]);

  const universal = filtered.filter((f) => f.scope === 'universal');
  const brand = filtered.filter((f) => f.scope === 'brand');

  const pick = (slug: string | null) => {
    onChange(slug);
    setIsOpen(false);
    setSearch('');
  };

  const sourceLabel =
    inherited.source === 'template' ? 'template'
    : inherited.source === 'brand' ? 'brand default'
    : inherited.source === 'universal' ? 'universal default'
    : 'default';

  const renderOption = (family: NonNullable<typeof selectedFamily>) => (
    <button
      key={family.slug}
      type="button"
      onClick={(e) => { e.stopPropagation(); pick(family.slug); }}
      className={`w-full flex items-center justify-between px-2 py-1 rounded text-left transition-colors ${
        value === family.slug ? 'bg-blue-500/30 text-white' : 'text-white/85 hover:bg-white/10'
      }`}
    >
      <span
        className="text-sm truncate"
        style={{ fontFamily: `"${family.cssFamily}", Inter, sans-serif` }}
      >
        {family.name}
      </span>
      {value === family.slug && <span className="text-blue-300 text-xs">✓</span>}
    </button>
  );

  // Trigger shows "Aa" in the effective font.
  const effectiveCss = selectedFamily
    ? `"${selectedFamily.cssFamily}", Inter, sans-serif`
    : inherited.slug && manifest
      ? `"${manifest.families.find((f) => f.slug === inherited.slug)?.cssFamily ?? 'Inter'}", Inter, sans-serif`
      : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        title={`Font: ${selectedFamily?.name ?? `Inherit (${inherited.name})`}`}
        aria-label="Font family"
        className={`w-6 h-6 flex items-center justify-center rounded border text-[11px] transition-colors ${
          value
            ? 'bg-blue-500 border-blue-400 text-white'
            : 'bg-black/40 border-white/20 text-white/80 hover:bg-black/60'
        }`}
        style={{ fontFamily: effectiveCss }}
      >
        Aa
      </button>
      {isOpen && coords && createPortal(
        <div
          ref={popoverRef}
          data-overlay-popover=""
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: coords.top,
            bottom: coords.bottom,
            left: coords.left,
            width: POPOVER_W,
            zIndex: Z.PICKER,
          }}
          className="bg-zinc-900/95 backdrop-blur rounded-md border border-white/15 shadow-xl p-2 flex flex-col gap-1.5"
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Search fonts…"
            className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs text-white placeholder-white/40 focus:outline-none"
          />
          <div className="max-h-52 overflow-y-auto flex flex-col gap-0.5">
            {/* Pinned inherit row */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); pick(null); }}
              className={`w-full flex items-center justify-between px-2 py-1 rounded text-left transition-colors ${
                value === null ? 'bg-blue-500/30 text-white' : 'text-white/85 hover:bg-white/10'
              }`}
            >
              <span className="text-sm truncate">
                Inherit — {inherited.name}
                <span className="text-white/50 text-[10px] ml-1">({sourceLabel})</span>
              </span>
              {value === null && <span className="text-blue-300 text-xs">✓</span>}
            </button>
            {universal.length > 0 && (
              <>
                <div className="text-[9px] uppercase tracking-wide text-white/50 px-2 pt-1">Universal</div>
                {universal.map(renderOption)}
              </>
            )}
            {brand.length > 0 && (
              <>
                <div className="text-[9px] uppercase tracking-wide text-white/50 px-2 pt-1">Brand</div>
                {brand.map(renderOption)}
              </>
            )}
            {universal.length === 0 && brand.length === 0 && (
              <div className="text-xs text-white/50 px-2 py-2">
                {manifest ? 'No fonts match' : 'No fonts in library'}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default FontPickerPopover;
